import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listBatches, archiveBatchManager } from "../../../api/batches";
import { useNavigate } from "react-router-dom";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { getBatchListExportColumns } from "../../../utils/listExportColumns";
import BatchTable from "../../../components/batches/BatchTable";
import PaginationFooter from "../../../components/common/PaginationFooter";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { batchListSortColumns } from "../../../config/tableSortColumns";
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
} from "../../../components/layout/ListPageLayout";

import { showConfirm, showAlert } from '../../../utils/popup';
import { apiErrorMessage } from '../../../utils/apiError';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const ManagerBatchList = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('active');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const res = await listBatches("MANAGER", { page: 0, size: 200 });
            setRows(res.data?.content ?? res.data ?? []);
        } catch {
            setRows([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        let result = rows;
        if (filter === 'active') {
            result = result.filter(r => r.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(r => r.active === false);
        }
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => String(x ?? '').toLowerCase();
            result = result.filter(r => {
                const hay = [r.name, r.id, r.batchId, r.levelName].map(pick).join(' ');
                return term.split(/\s+/).every(tok => hay.includes(tok));
            });
        }
        return result;
    }, [rows, dq, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: batchListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const onEdit = (row) => navigate(`/manager/batches/${row.batchId || row.id}/edit`);
    const onSessions = (row) => navigate(`/manager/batches/${row.batchId || row.id}/sessions`);
    const onArchive = async (row) => {
        if (!await showConfirm(t('batches.archiveConfirm'))) return;
        try {
            await archiveBatchManager(row.batchId || row.id);
            load();
        } catch (err) {
            showAlert(apiErrorMessage(err, t('batches.archiveFailed', { defaultValue: 'Could not archive batch.' })));
        }
    };

    const shown = filtered.length;
    const total = filter === 'all' ? rows.length : filter === 'active' ? rows.filter(r => r.active !== false).length : rows.filter(r => r.active === false).length;

    const filterTabs = [
        { key: "all", label: t('common.all'), count: rows.length },
        { key: "active", label: t('batches.active'), count: rows.filter(r => r.active !== false).length },
        { key: "archived", label: t('batches.archived'), count: rows.filter(r => r.active === false).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('batches.title')}
                subtitle={t('batches.managerSubtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getBatchListExportColumns(t)}
                            data={filtered}
                            filename="batches"
                            disabled={filtered.length === 0}
                        />
                        <button type="button" onClick={() => navigate("/manager/batches/new")} className="btn-page-action-primary">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('batches.add')}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={filterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('batches.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('batches.entityLabel')}
            />

            <BatchTable
                role="MANAGER"
                rows={pagination.pageItems}
                loading={loading}
                loadError={loadError}
                onEdit={onEdit}
                onArchive={onArchive}
                onSessions={onSessions}
            />

            <PaginationFooter
                page={pagination.page}
                size={pagination.size}
                total={pagination.total}
                onPageChange={pagination.setPage}
                onSizeChange={pagination.setSize}
            />
        </ListPageLayout>
    );
};

export default ManagerBatchList;
